<template>
    <v-dialog :value="show" max-width="800" persistent>
        <v-card>
            <v-card-title class="headline">
                Atualizar tarifas da reserva cancelada
            </v-card-title>
 
            <v-card-text>
                <p class="mb-4">
                    A reserva será <strong>cancelada</strong>. Você pode corrigir as tarifas abaixo para evitar novas recusas pelo mesmo motivo.
                </p>

                <div v-for="group in groupedRooms" :key="group.key" class="room-fare-section mb-5">
                    <div class="room-header">
                        <div>
                            <div class="room-name">{{ group.name }}</div>
                            <div v-if="group.isCombined" class="room-summary">
                                Média exibida na reserva: <strong>{{ formatCurrency(group.combinedAverageAmount) }}/noite</strong>
                                <span v-if="group.combinedTotalNights"> · {{ group.combinedTotalNights }} {{ group.combinedTotalNights > 1 ? 'noites' : 'noite' }}</span>
                            </div>
                        </div>

                        <v-tooltip v-if="group.isCombined" bottom max-width="300">
                            <template v-slot:activator="{ on, attrs }">
                                <v-chip
                                    small
                                    color="primary"
                                    v-bind="attrs"
                                    v-on="on"
                                >
                                    Tarifa combinada
                                    <v-icon small right>mdi-information-outline</v-icon>
                                </v-chip>
                            </template>
                            <span>Este quarto possui uma tarifa combinada, uma diária média formada por tarifas com valores diferentes. As tarifas abaixo são as originais que compõem essa combinação.</span>
                        </v-tooltip>
                    </div>

                    <div class="fare-lines">
                        <div v-for="room in group.rooms" :key="room.id + '_' + room.fare.id" class="fare-line">
                            <div class="fare-line-main">
                                <div class="fare-line-label">
                                    <span class="fare-line-title">
                                        <strong>{{ room.combinedTitle || room.combinedLabel || 'Tarifa atual' }}</strong>
                                        <span v-if="room.combinedNightsLabel"> · {{ room.combinedNightsLabel }}</span>
                                    </span>
                                    <small v-if="room.applicableDaysLabel">Disponível: {{ room.applicableDaysLabel }}</small>
                                    <small>Máximo permitido: {{ maxAllowedDisplayFor(room.fare) }} (+15%)</small>
                                </div>
                                <div class="fare-current">
                                    <span class="label-text">Valor atual</span>
                                    <strong class="current-amount-value">{{ formatCurrency(room.fare.amount) }}</strong>
                                </div>
                            </div>

                            <div class="fare-line-actions">
                                <v-text-field
                                    v-model="fareStates[room.fare.id].amount"
                                    label="Novo valor da diária"
                                    prefix="R$"
                                    type="number"
                                    step="0.01"
                                    min="0.01"
                                    :error-messages="errorMessagesFor(room.fare)"
                                    @input="onAmountInput(room.fare.id)"
                                    outlined
                                    dense
                                    hide-details="auto"
                                />

                                <v-btn-toggle
                                    v-if="isMultiFare"
                                    v-model="fareStates[room.fare.id].confirmed"
                                    @change="onConfirmedChange(room.fare.id, room.fare, $event)"
                                    :disabled="cannotConfirmValue && !isConfirmed(room.fare.id)"
                                    class="confirm-check"
                                >
                                    <v-btn
                                        :value="true"
                                        text
                                        :disabled="cannotConfirmValue && !isConfirmed(room.fare.id)"
                                        class="rounded-lg"
                                        color="primary"
                                        outlined
                                    >
                                        <span class="btn-label">Manter valor</span>
                                    </v-btn>
                                </v-btn-toggle>
                            </div>

                            <div v-if="fareStates[room.fare.id] && fareStates[room.fare.id].amount && isValidAmountFor(room.fare)" class="preview">
                                <span class="label-text">Prévia: </span>
                                <span>{{ formatCurrency(room.fare.amount) }}</span>
                                <v-icon small class="mx-1">mdi-arrow-right</v-icon>
                                <strong>{{ formatCurrency(newAmountInCentsFor(room.fare.id)) }}</strong>
                            </div>
                        </div>
                    </div>
                </div>

                <p v-if="faresValidationErrorMessage" class="fares-validation-error red--text">
                    {{ faresValidationErrorMessage }}
                </p>

                <div class="fare-summary mt-2">
                    {{ updatedFaresCount }} tarifa(s) alterada(s) · {{ confirmedFaresCount }} mantida(s)
                </div>
                <p class="warning-text mt-1">
                    A atualização refletirá em novas reservas deste quarto. A reserva atual será cancelada normalmente.
                </p>

                <v-alert type="warning" outlined dense class="mt-4 mb-0 body-2">
                    Todas as tarifas cujos valores não forem atualizados ficarão <strong>indisponíveis</strong> e não aparecerão mais na plataforma.
                </v-alert>
            </v-card-text>

            <v-card-actions class="px-6 pb-4">
                <v-btn text @click="requestDismiss">
                    Cancelar sem atualizar
                </v-btn>
                <v-spacer />
                <v-btn
                    color="primary"
                    :disabled="hasAnyError || !areFaresValidated"
                    @click="handleConfirm"
                >
                    Confirmar atualização
                </v-btn>
            </v-card-actions>
        </v-card>
        <v-dialog :value="showDismissConfirm" max-width="480" persistent>
            <v-card>
                <v-card-title class="headline">
                    Cancelar sem atualizar tarifas?
                </v-card-title>
                <v-card-text>
                    Caso o cancelamento seja confirmado, todas as tarifas desta reserva ficarão <strong>indisponíveis</strong> e não aparecerão mais na plataforma. 
                </v-card-text>
                <v-card-actions class="px-6 pb-4">
                    <v-btn text @click="showDismissConfirm = false">
                        Voltar
                    </v-btn>
                    <v-spacer />
                    <v-btn color="error" @click="confirmDismiss">
                        Cancelar e inativar tarifas
                    </v-btn>
                </v-card-actions>
            </v-card>
        </v-dialog>
    </v-dialog>
</template>

<script>
export default {
    name: 'UpdateFareOnCancellationModal',

    props: {
        show: {
            type: Boolean,
            required: true,
        },
        rooms: {
            type: Array,
            default: function() { return [] },
        },
    },

    data: () => ({
        fareStates: {},
        uniqueRooms: [],
        showDismissConfirm: false,
    }),

    computed: {
        hasAnyError () {
            return this.uniqueRooms.some(function(room) {
                const state = this.fareStates[room.fare.id]
                const val = state && state.amount
                return val && !this.isValidAmountFor(room.fare)
            }, this)
        },

        fareUpdates () {
            const self = this
            return this.uniqueRooms
                .filter(function(room) {
                    const state = self.fareStates[room.fare.id]
                    return state && self.isValidAmountFor(room.fare)
                })
                .map(function(room) {
                    return {
                        fareId: room.fare.id,
                        newAmountInCents: self.newAmountInCentsFor(room.fare.id),
                        isConfirming: self.fareStates[room.fare.id].confirmed
                    }
                })
        },

        isMultiFare () {
            return this.rooms && this.rooms.length > 1;
        },

        cannotConfirmValue () {
            const confirmedCount = this.uniqueRooms.filter(function(room) {
                return this.fareStates[room.fare.id] && this.fareStates[room.fare.id].confirmed
            }, this).length;

            return confirmedCount >= (this.uniqueRooms.length - 1);
        },

        areFaresValidated () {
            let valid = false;

            Object.keys(this.fareStates).forEach(function (fareId) {
                const state = this.fareStates[fareId]
                if (state.amount !== null) {
                    valid = true;
                }
            }, this);

            return valid;
        },

        faresValidationErrorMessage () {
            return this.areFaresValidated
                ? null
                : 'Você deve atualizar ou confirmar o valor de pelo menos um quarto.';
        },

        groupedRooms () {
            const groups = []
            const groupIndexes = {}

            this.uniqueRooms.forEach(function(room) {
                const key = room.isCombined && room.combinedGroupId
                    ? room.combinedGroupId
                    : room.id + '_' + room.fare.id

                if (groupIndexes[key] === undefined) {
                    groupIndexes[key] = groups.length
                    groups.push({
                        key: key,
                        name: room.name || 'Acomodação',
                        isCombined: !!room.isCombined,
                        combinedAverageAmount: room.combinedAverageAmount || room.fare.amount,
                        combinedTotalNights: room.combinedTotalNights,
                        rooms: [],
                    })
                }

                groups[groupIndexes[key]].rooms.push(room)
            })

            return groups
        },

        updatedFaresCount () {
            return this.uniqueRooms.filter(function(room) {
                const state = this.fareStates[room.fare.id]
                return state && state.amount && !state.confirmed && this.isValidAmountFor(room.fare)
            }, this).length
        },

        confirmedFaresCount () {
            return this.uniqueRooms.filter(function(room) {
                const state = this.fareStates[room.fare.id]
                return state && state.confirmed
            }, this).length
        },
    },

    methods: {
        newAmountInCentsFor: function(fareId) {
            const state = this.fareStates[fareId]
            const val = state && state.amount
            if (!val) return null;
            return Math.round(parseFloat(val) * 100)
        },

        maxAllowedCentsFor: function(fare) {
            return Math.round(fare.amount * 1.15)
        },

        maxAllowedDisplayFor: function(fare) {
            return this.formatCurrency(this.maxAllowedCentsFor(fare))
        },

        isValidAmountFor: function(fare) {
            const state = this.fareStates[fare.id]
            if (!(state && state.amount)) return true
            const cents = this.newAmountInCentsFor(fare.id)
            const isValidBasedOnOriginal = state.confirmed ? true : cents !== fare.amount;
            return cents > 0 && cents <= this.maxAllowedCentsFor(fare) && isValidBasedOnOriginal;
        },

        errorMessagesFor: function(fare) {
            const state = this.fareStates[fare.id]
            if (!(state && state.amount)) return []
            const cents = this.newAmountInCentsFor(fare.id)
            if (cents <= 0) return ['O valor deve ser maior que zero.']
            if (cents === fare.amount && !state.confirmed) return ['O novo valor deve ser diferente do valor atual.']
            if (cents > this.maxAllowedCentsFor(fare)) {
                return ['O valor não pode ser superior a 15% do valor atual (' + this.maxAllowedDisplayFor(fare) + ').']
            }
            return []
        },

        onConfirmedChange: function(fareId, fare, confirmed) {
            this.fareStates[fareId].amount = confirmed
                ? (fare.amount / 100).toFixed(2)
                : null;
        },

        onAmountInput: function(fareId) {
            if (this.fareStates[fareId] && this.fareStates[fareId].confirmed) {
                this.fareStates[fareId].confirmed = false;
            }
        },

        formatCurrency: function(cents) {
            return (cents / 100).toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' })
        },

        requestDismiss: function() {
            this.showDismissConfirm = true;
        },

        confirmDismiss: function() {
            this.showDismissConfirm = false;
            this.$emit('dismiss');
        },

        handleConfirm: function() {
            if (this.hasAnyError || !this.areFaresValidated) {
                return;
            }

            this.$emit('confirm', this.fareUpdates)
            this.fareStates = {}
        },

        setUniqueRooms () {
            const seen = new Set()
            this.uniqueRooms = this.rooms.filter(function(room) {
                const key = room.id + '_' + room.fare.id
                if (seen.has(key)) return false
                seen.add(key)
                return true
            });
        },
        setFareStatus () {
            const fareStates = {};
            this.uniqueRooms.forEach(function (room) {
                fareStates[room.fare.id] = {
                    amount: null,
                    confirmed: false
                }
            })

            this.fareStates = fareStates;
        },
        isConfirmed (roomFareId) {
            return this.fareStates[roomFareId].confirmed;
        },
    },

    watch: {
        rooms: {
            immediate: true,
            handler: function() {
                this.setUniqueRooms();
                this.setFareStatus();
            },
        },
        show: function(val) {
            if (!val) {
                this.fareStates = {}
                this.showDismissConfirm = false
            }
        },
    },
}
</script>

<style scoped>
.label-text {
    color: #54667a;
    margin-right: 8px;
    font-size: 0.875rem !important;
}
.room-name {
    font-weight: 600;
    color: #1f2933;
    font-size: 15px;
}

.room-header {
    display: flex;
    align-items: flex-start;
    justify-content: space-between;
    gap: 12px;
}

.room-summary,
.fare-summary {
    color: #54667a;
    font-size: 0.8125rem;
}

::v-deep .confirm-check .btn-label {
    font-size: 0.75rem;
    text-transform: capitalize !important;
}

::v-deep .confirm-check,
::v-deep .confirm-check .v-btn {
    min-height: 38px !important;
    height: 38px !important;
}

::v-deep .confirm-check .v-btn {
    min-width: 136px;
}

.preview {
    display: flex;
    align-items: center;
    margin-top: 8px;
    font-size: 0.8125rem;
}

p.fares-validation-error {
    font-size: 0.75rem;
}

.warning-text {
    color: #54667a;
    font-size: 13px;
}

.room-fare-section {
    border: 1px solid #e0e0e0;
    border-radius: 8px;
    padding: 14px;
}

.fare-lines {
    display: flex;
    flex-direction: column;
    gap: 12px;
}

.fare-line {
    border-top: 1px solid #eeeeee;
    padding-top: 12px;
}

.fare-line:first-child {
    border-top: 0;
    padding-top: 0;
}

.fare-line-main,
.fare-line-actions {
    display: grid;
    grid-template-columns: minmax(0, 1fr) 170px;
    gap: 16px;
    align-items: start;
}

.fare-line-actions {
    grid-template-columns: minmax(180px, 1fr) auto;
    margin-top: 8px;
}

.fare-line-label {
    display: flex;
    flex-direction: column;
    color: #1f2933;
}

.fare-line-title strong {
    font-weight: 600;
}

.fare-line-label small {
    color: #54667a;
    font-weight: 400;
    margin-top: 2px;
}

.fare-current {
    display: flex;
    flex-direction: column;
    align-items: flex-end;
    font-size: 0.8125rem;
}

.current-amount-value {
    color: #1f2933;
    font-size: 1rem;
    line-height: 1.2;
}

@media (max-width: 600px) {
    .room-header,
    .fare-line-main,
    .fare-line-actions {
        display: flex;
        flex-direction: column;
        gap: 8px;
    }

    .fare-current {
        align-items: flex-start;
    }
}
</style>
